import { lazy, Suspense } from 'react'
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NotFound } from './pages/NotFound'

import { useCookies } from 'react-cookie'
import { I18nextProvider } from 'react-i18next'
import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider
} from 'react-router'
import { Toaster } from 'sonner'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Loading } from './components/Loading'
import { TooltipProvider } from './components/ui/tooltip'
import { PermissionsProvider } from './contexts/PermissionsContext'
import { i18nInstance } from './i18n'
import { msalClient } from './util/msal'
import { initSentry } from './util/sentry'

// The Adyen checkout SDK is ~1.4 MB and is only reachable from these routes.
// Loading them lazily keeps it out of the chunk every admin page downloads;
// named exports need the .then() mapping because React.lazy expects a default.
const PaymentPage = lazy(() =>
  import('./pages/PaymentPage').then((m) => ({ default: m.PaymentPage }))
)
const PaymentSuccessPage = lazy(() =>
  import('./pages/PaymentSuccessPage').then((m) => ({
    default: m.PaymentSuccessPage
  }))
)
const PaymentFailedPage = lazy(() =>
  import('./pages/PaymentFailedPage').then((m) => ({
    default: m.PaymentFailedPage
  }))
)
const PaymentReturnPage = lazy(() =>
  import('./pages/PaymentReturnPage').then((m) => ({
    default: m.PaymentReturnPage
  }))
)
const TestAdyenPayment = lazy(() =>
  import('./pages/TestAdyenPayment').then((m) => ({
    default: m.TestAdyenPayment
  }))
)

// Admin pages, split the same way: each is only reachable from its own route,
// so an admin who never opens Share Rules never downloads it.
const AdminCostCoverageCodes = lazy(() => import('./pages/AdminCostCoverageCodes'))
const AdminCostCoverageReservations = lazy(() => import('./pages/AdminCostCoverageReservations'))
const AdminPendingTransfers = lazy(() => import('./pages/AdminPendingTransfers'))
const AdminShareRules = lazy(() => import('./pages/AdminShareRules'))
const AdminSubMerchants = lazy(() => import('./pages/AdminSubMerchants'))
const Invoicing = lazy(() => import('./pages/Invoicing'))
const AdminPayments = lazy(() =>
  import('./pages/AdminPayments').then((m) => ({ default: m.AdminPayments }))
)
const PaymentDetails = lazy(() =>
  import('./pages/PaymentDetails').then((m) => ({ default: m.PaymentDetails }))
)

initSentry()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
})

function RootRedirect() {
  const isMsalRedirectResponse =
    window.location.hash.includes('code=') &&
    window.location.hash.includes('state=')

  if (isMsalRedirectResponse) {
    return <Loading />
  }

  return <Navigate to="/admin/payments" replace />
}

// Lazy routes need a Suspense boundary. One per route rather than a single one
// around RouterProvider, so a cold load of one route cannot blank the others.
//
// Each of these pages renders its own layout (LayoutWithoutMenu, AppLayout)
// from inside the lazy chunk, so that layout is suspended too - a cold load
// shows the bare Loading fallback, not a chrome-with-spinner. Rendering the
// shell during the fetch would mean hoisting the layouts into parent routes,
// which is a larger change than the chunking this commit is doing.
const lazyRoute = (element: React.ReactNode) => (
  <Suspense fallback={<Loading />}>{element}</Suspense>
)

const routes: RouteObject[] = [
  {
    path: '/pay/:invoiceId',
    element: lazyRoute(<PaymentPage />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/payment-success/:invoiceId',
    element: lazyRoute(<PaymentSuccessPage />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/payment-failed/:invoiceId',
    element: lazyRoute(<PaymentFailedPage />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/payment-return/:invoiceId',
    element: lazyRoute(<PaymentReturnPage />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/',
    element: <RootRedirect />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin',
    element: <Navigate to="/admin/payments" replace />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/request-adyen-payment',
    element: lazyRoute(<TestAdyenPayment />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/payments',
    element: lazyRoute(<AdminPayments />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/payments/:invoiceId',
    element: lazyRoute(<PaymentDetails />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/sub-merchants',
    element: lazyRoute(<AdminSubMerchants />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/invoicing',
    element: lazyRoute(<Invoicing />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/share-rules',
    element: lazyRoute(<AdminShareRules />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/share-rules/create',
    element: lazyRoute(<AdminShareRules />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/share-rules/view/:ruleId',
    element: lazyRoute(<AdminShareRules />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/share-rules/edit/:ruleId',
    element: lazyRoute(<AdminShareRules />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/cost-coverage-codes',
    element: lazyRoute(<AdminCostCoverageCodes />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/cost-coverage-reservations',
    element: lazyRoute(<AdminCostCoverageReservations />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/admin/pending-transfers',
    element: lazyRoute(<AdminPendingTransfers />),
    errorElement: <ErrorBoundary />
  },
  {
    path: '*',
    element: <NotFound />,
    errorElement: <ErrorBoundary />
  }
]

const router = createBrowserRouter(routes)

function App() {
  const cookies = useCookies(['lang'])[0]
  const i18n = i18nInstance(cookies.lang || 'de')
  return (
    <TooltipProvider delayDuration={0}>
      <MsalProvider instance={msalClient()}>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <PermissionsProvider>
              <RouterProvider router={router} />
              <Toaster position="top-right" richColors />
            </PermissionsProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </MsalProvider>
    </TooltipProvider>
  )
}

export default App
